import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './LandingPreview.css';

const PATHS = [
  {
    id: 'cypher',
    kicker: 'Open booth',
    title: 'Cypher',
    line: 'One beat. Many rappers. Every take goes live as you record.',
    steps: ['Producer posts a cypher beat', 'Any rapper records in the browser', 'The verse is public on the spot'],
  },
  {
    id: 'collab',
    kicker: 'One mix',
    title: 'Collab',
    line: 'One beat. One release. The producer picks the verse the world hears.',
    steps: ['Producer posts a collab beat', 'Rappers send private takes', 'One mix is published. Recording closes.'],
  },
] as const;

const ROLES = [
  {
    id: 'rapper',
    title: 'Rapper',
    body: 'Jump a cypher or send a take on a collab beat. Delete your own recording any time.',
  },
  {
    id: 'producer',
    title: 'Producer',
    body: 'Upload as cypher or collab. Review takes, publish one mix, or ping the rapper to redo.',
  },
  {
    id: 'listener',
    title: 'Listener',
    body: 'Play finished tracks and cypher drops. Vote on what you like.',
  },
] as const;

const LandingPreview: React.FC = () => {
  return (
    <div className="lpv">
      <Helmet>
        <title>DHH Collab | Landing preview</title>
        <meta
          name="robots"
          content="noindex"
        />
      </Helmet>

      <div className="lpv__bg" aria-hidden>
        <span className="lpv__blob lpv__blob--1" />
        <span className="lpv__blob lpv__blob--2" />
        <span className="lpv__grid" />
      </div>

      <header className="lpv__hero lpv__wrap container-padding">
        <span className="lpv__badge">Design preview</span>
        <p className="lpv__eyebrow">Desi Hip-Hop · India</p>
        <h1 className="lpv__title">
          Beats up. Bars down.
          <span className="lpv__title-accent"> Mixes out.</span>
        </h1>
        <p className="lpv__lede">
          Record in the browser. Cyphers go live instantly. Collabs stay private until the
          producer publishes one mix.
        </p>
        <div className="lpv__cta-row">
          <Link to="/signup" className="lpv__btn lpv__btn--primary">
            Get started
          </Link>
          <Link to="/login" className="lpv__btn lpv__btn--ghost">
            Sign in
          </Link>
        </div>
        <ul className="lpv__chips" aria-label="What you can do">
          <li>Cypher</li>
          <li>Collab + approval</li>
          <li>Artist DMs</li>
        </ul>
      </header>

      <section className="lpv__section lpv__wrap container-padding" aria-labelledby="lpv-paths">
        <header className="lpv__head">
          <h2 id="lpv-paths" className="lpv__h2">
            Two ways a beat becomes a track
          </h2>
          <p className="lpv__sub">The difference is who decides it goes public.</p>
        </header>
        <div className="lpv__paths">
          {PATHS.map(path => (
            <article key={path.id} className={`lpv-path lpv-path--${path.id}`}>
              <span className="lpv-path__kicker">{path.kicker}</span>
              <h3 className="lpv-path__title">{path.title}</h3>
              <p className="lpv-path__line">{path.line}</p>
              <ol className="lpv-path__steps">
                {path.steps.map(step => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            </article>
          ))}
        </div>
      </section>

      <section className="lpv__section lpv__wrap container-padding" aria-labelledby="lpv-gate">
        <header className="lpv__head">
          <h2 id="lpv-gate" className="lpv__h2">
            Collab approval
          </h2>
          <p className="lpv__sub">Other rappers never see a pending take. Listeners only hear the mix.</p>
        </header>
        <ol className="lpv-gate">
          <li className="lpv-gate__step">
            <span className="lpv-gate__num">01</span>
            <p>
              <strong>Record</strong>
              Rapper drops a verse on a collab beat. It lands in the producer’s queue.
            </p>
          </li>
          <li className="lpv-gate__step">
            <span className="lpv-gate__num">02</span>
            <p>
              <strong>Review</strong>
              Producer plays the full mix, then publishes, passes, or DMs for a redo.
            </p>
          </li>
          <li className="lpv-gate__step">
            <span className="lpv-gate__num">03</span>
            <p>
              <strong>Release</strong>
              One mix goes live. Recording on that beat closes.
            </p>
          </li>
        </ol>
      </section>

      <section className="lpv__section lpv__wrap container-padding" aria-labelledby="lpv-dm">
        <div className="lpv-dm">
          <div>
            <h2 id="lpv-dm" className="lpv__h2">
              Artists can ping each other
            </h2>
            <p className="lpv__sub lpv__sub--flush">
              1:1 messages between producers and rappers. Share a beat or a verse. Ask for
              another take without leaving the site.
            </p>
          </div>
          <p className="lpv-dm__note">Listeners don’t get a chat inbox — the booth stays with artists.</p>
        </div>
      </section>

      <section className="lpv__section lpv__wrap container-padding" aria-labelledby="lpv-roles">
        <header className="lpv__head">
          <h2 id="lpv-roles" className="lpv__h2">
            Pick a lane
          </h2>
        </header>
        <div className="lpv-roles">
          {ROLES.map(role => (
            <article key={role.id} className="lpv-role">
              <h3>{role.title}</h3>
              <p>{role.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="lpv__section lpv__section--end lpv__wrap container-padding">
        <div className="lpv-end">
          <h2 className="lpv__h2">Ready when you are.</h2>
          <p className="lpv__sub lpv__sub--flush">Free account. Record in the browser. No extra software.</p>
          <Link to="/signup" className="lpv__btn lpv__btn--primary">
            Create an account
          </Link>
        </div>
      </section>
    </div>
  );
};

export default LandingPreview;
